import React, { ReactElement } from 'react'
import { useInjectExternalSources } from '../Hooks/useInjectExternalSources'
import { useMountExternalComponent } from '../Hooks/useMountExternalComponent'
import { ExternalComponentConfig, useExternalComponentConfig } from '../Hooks/useExternalComponentConfig'
import Loader from '../../../../../React/Common/Components/Loader'

type Props = {
    componentKey: string
    mountOptionsOverrides: Record<string, unknown>
}

/**
 * ExternalComponent is responsible for rendering any external component within the CCS application.
 * It retrieves the necessary configuration for the external component (such as JS/CSS links, component ID and mount options) using the provided componentKey,
 * injects the external sources into the document, and mounts the external component onto a div with the specified component ID.
 *
 * @param componentKey - A unique key used to retrieve the external component's configuration.
 * @param mountOptionsOverrides - An object containing options that will override the default mount options specified in the configuration.
 *
 * @returns A React element that serves as a container for the external component, with the necessary external sources injected and the component mounted.
 */
export const ExternalComponent = ({ componentKey, mountOptionsOverrides }: Props): ReactElement => {
    const { jsLink, cssLink, componentID, mountOptions }: ExternalComponentConfig =
        useExternalComponentConfig(componentKey)

    useInjectExternalSources(jsLink, cssLink)

    useMountExternalComponent(componentID, {
        ...mountOptions,
        ...mountOptionsOverrides
    })

    return (
        <div className='external-component' id={componentID} style={{ isolation: 'isolate' }}>
            <Loader />
        </div>
    )
}
